/*
 * Reading a public endpoint, with no session and no way to acquire one.
 *
 * `client.ts` beside this file is the product's one `fetch` and it is the wrong
 * shape here — not because it is too big, though it is, but because of what it
 * carries: an access token, a refresh path, a session-ended callback and a
 * credentialed cookie policy. `status.pushcaster.io` holds none of those, is
 * deliberately absent from both CORS allowlists (V1_2_SPEC.md §6.4), and must
 * stay that way; a client that *could* attach a credential on that origin is
 * the one mistake that turns a public page into a way to read authenticated
 * responses. So this is a second `fetch`, in the one directory where §36 A5
 * permits one, and it is incapable of authenticating rather than merely
 * declining to.
 *
 * ── The failure kind is the product, not an afterthought ────────────────────
 *
 * V1_2_SPEC.md §5.3 requires the status page to name the class of a failure —
 * network, timeout, or server error — and never to show a stack trace. That is
 * only possible if the distinction survives the call, and `fetch` erases it: a
 * DNS failure, a CORS refusal and an aborted request all arrive as the same
 * `TypeError`. So each is caught where it is still distinguishable and carried
 * out as `kind`.
 *
 * `shape` is the fourth, and it is the one §5.3 names that nothing else would
 * have caught: a response that arrives, with 200, containing something this
 * build does not recognise. Treating that as success is how a status page
 * renders an empty component list as "nothing is wrong".
 */

/** The four classes §5.3 asks the page to distinguish. Never a stack trace. */
export type PublicReadFailureKind =
  /** The request never reached a server: DNS, TLS, offline, or a CORS refusal. */
  | 'network'
  /** No answer inside the deadline. Distinct from `network`: something is there. */
  | 'timeout'
  /** A server answered, and not with success. `status` carries which. */
  | 'server'
  /** A 200 whose body this build does not recognise. */
  | 'shape'

export class PublicReadError extends Error {
  readonly kind: PublicReadFailureKind
  /** The HTTP status, on `server` only. `null` for every other kind. */
  readonly status: number | null

  constructor(kind: PublicReadFailureKind, message: string, status: number | null = null) {
    super(message)
    this.name = 'PublicReadError'
    this.kind = kind
    this.status = status
  }
}

export interface PublicReaderOptions {
  /** Origin only — `https://api.pushcaster.io`. The base path is appended here. */
  readonly origin: string
  /** How long to wait before giving up. Defaults to 8 s. */
  readonly timeoutMs?: number
  /**
   * Injectable for tests. When omitted, the global is resolved **at call
   * time**, not here.
   *
   * That distinction is not academic. A default parameter captures
   * `globalThis.fetch` at construction, and this reader is built once and held
   * for the life of the page — so it would pin whatever `fetch` existed at
   * module-init time and keep calling it after any later replacement. A test
   * that stubs the global sees the old one; so would a page whose environment
   * installs a polyfill after the first import.
   */
  readonly fetch?: typeof globalThis.fetch
}

/** Parses an unknown body into `T`, or throws. A throw becomes a `shape` failure. */
export type Parse<T> = (body: unknown) => T

export interface PublicReader {
  read: <T>(path: string, parse: Parse<T>, options?: { signal?: AbortSignal }) => Promise<T>
}

const BASE_PATH = '/api/v1'
const DEFAULT_TIMEOUT_MS = 8_000

export function createPublicReader({
  origin,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  fetch: fetchImpl,
}: PublicReaderOptions): PublicReader {
  async function read<T>(
    path: string,
    parse: Parse<T>,
    options: { signal?: AbortSignal } = {},
  ): Promise<T> {
    /*
     * A manual controller rather than `AbortSignal.timeout`, because the two
     * abort reasons have to stay apart. A deadline that fired is a `timeout` the
     * reader must report; a caller that navigated away is not a failure at all,
     * and reporting it as one would flash "the request timed out" on a page the
     * reader has already left.
     */
    const deadline = new AbortController()
    const timer = setTimeout(() => {
      deadline.abort()
    }, timeoutMs)
    const abortOnCallerSignal = () => {
      deadline.abort()
    }
    options.signal?.addEventListener('abort', abortOnCallerSignal, { once: true })

    let response: Response
    try {
      response = await (fetchImpl ?? globalThis.fetch)(`${origin}${BASE_PATH}${path}`, {
        method: 'GET',
        /* Structurally incapable of sending or receiving a cookie. Not a
           preference — see the header of this file. */
        credentials: 'omit',
        /* No `Authorization`, and no `Accept-Language`: the response does not
           vary by locale (D6) and a varying request header would fragment the
           15-second edge cache this endpoint depends on during an incident. */
        headers: { accept: 'application/json' },
        /* The endpoint sets its own `Cache-Control` (§6.4); this asks the
           browser not to hold a *second*, longer copy on top of it. */
        cache: 'no-store',
        signal: deadline.signal,
      })
    } catch (cause) {
      if (options.signal?.aborted === true) throw cause
      if (deadline.signal.aborted) {
        throw new PublicReadError('timeout', `No response within ${timeoutMs} ms.`)
      }
      throw new PublicReadError('network', 'The request did not reach a server.')
    } finally {
      clearTimeout(timer)
      options.signal?.removeEventListener('abort', abortOnCallerSignal)
    }

    if (!response.ok) {
      throw new PublicReadError(
        'server',
        `The server answered ${response.status}.`,
        response.status,
      )
    }

    let body: unknown
    try {
      body = await response.json()
    } catch {
      throw new PublicReadError('shape', 'The response was not JSON.')
    }

    try {
      return parse(body)
    } catch {
      /* Deliberately not the parser's own message. It names fields, and §4.2
         forbids this page from putting internal shape detail on screen. */
      throw new PublicReadError('shape', 'The response was not in a recognised form.')
    }
  }

  return { read }
}
